import React from "react";
import PropTypes from "prop-types";
import { defineMessages, useIntl } from "react-intl";
import cx from "classnames";
import { Message } from "semantic-ui-react";
import { flattenToAppURL } from "@plone/volto/helpers/Url/Url";

import LeadImageSidebar from "./LeadImageSidebar";
import SidebarPortal from "@plone/volto/components/manage/Sidebar/SidebarPortal";
import config from "@plone/volto/registry";

import imageBlockSVG from "@plone/volto/components/manage/Blocks/Image/block-image.svg";

const messages = defineMessages({
  ImageBlockInputPlaceholder: {
    id: "Upload a lead image in the 'Lead Image' content field.",
    defaultMessage: "Upload a lead or preview image in the content fields.",
  },
});

/**
 * Determine which image field and source strategy is available.
 *
 * Priority:
 * 1. properties.image              — News Item lead image (embedded)
 * 2. properties.preview_image      — Preview image behavior (embedded)
 * 3. properties.preview_image_link — Linked preview image object (relation field)
 *
 * Returns one of:
 *   { strategy: 'field', imageField: string, imageData: object }
 *   { strategy: 'linked', basePath: string }
 *   null
 */
const getImageStrategy = (properties) => {
  if (properties.image) {
    return {
      strategy: "field",
      imageField: "image",
      imageData: properties.image,
    };
  }
  if (properties.preview_image) {
    return {
      strategy: "field",
      imageField: "preview_image",
      imageData: properties.preview_image,
    };
  }
  if (properties.preview_image_link?.["@id"]) {
    const basePath = flattenToAppURL(properties.preview_image_link["@id"]);
    return { strategy: "linked", basePath };
  }
  return null;
};

const Edit = (props) => {
  const intl = useIntl();
  const { data, properties, selected } = props;

  const placeholder = () =>
    data.placeholder || intl.formatMessage(messages.ImageBlockInputPlaceholder);

  const Image = config.getComponent({ name: "Image" }).component;

  const imageStrategy = getImageStrategy(properties);
  const altText = data.image_caption || properties.image_caption || "";
  const className = cx("responsive", { "full-image": data.align === "full" });

  const getSizes = () => {
    if (data.align === "full" || data.align === "center") return "100vw";
    if (data.align === "left" || data.align === "right") return "50vw";
    return undefined;
  };

  return (
    <div
      className={cx(
        "block image align",
        {
          center: !Boolean(data.align),
        },
        data.align,
      )}
    >
      {!imageStrategy && (
        <Message>
          <center>
            <Image src={imageBlockSVG} alt="" />
            <div className="message-text">{placeholder()}</div>
          </center>
        </Message>
      )}

      {imageStrategy?.strategy === "field" &&
        (() => {
          const { imageField, imageData } = imageStrategy;
          if (imageData.data) {
            // Freshly uploaded — use base64 blob
            return (
              <Image
                className={className}
                src={`data:${imageData["content-type"]};base64,${imageData.data}`}
                width={imageData.width}
                height={imageData.height}
                alt={altText}
                style={{
                  aspectRatio: `${imageData.width}/${imageData.height}`,
                }}
              />
            );
          }
          // Saved embedded image — use item + imageField
          return (
            <Image
              className={className}
              item={properties}
              imageField={imageField}
              sizes={getSizes()}
              alt={altText}
            />
          );
        })()}

      {imageStrategy?.strategy === "linked" && (
        <img
          className={cx("ui image", className)}
          src={`${imageStrategy.basePath}/@@images/image/preview`}
          alt={altText}
          loading="lazy"
          width="100%"
          height="auto"
          // style={{ aspectRatio: "16/9" }}
        />
      )}

      <SidebarPortal selected={selected}>
        <LeadImageSidebar {...props} />
      </SidebarPortal>
    </div>
  );
};

export default Edit;
Edit.propTypes = {
  properties: PropTypes.objectOf(PropTypes.any).isRequired,
  selected: PropTypes.bool.isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  pathname: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  openObjectBrowser: PropTypes.func.isRequired,
};
